import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Check, ChevronRight, Loader2, Image as ImageIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { useImageGeneration } from '.';
import { cn } from '@/lib/utils';
import { useModels } from '@/lib/hooks/queries/useModels';
import { UserModel } from '@/lib/types/userModels';

// Helper function to determine model status
const getModelStatus = (model: UserModel): 'training' | 'active' => {
  if (!model.training_started_at || !model.training_finished_at) {
    return 'active';
  }

  const now = new Date();
  const startedAt = new Date(model.training_started_at);
  const finishedAt = new Date(model.training_finished_at);

  if (now >= startedAt && now <= finishedAt) {
    return 'training';
  }

  return 'active';
};

export default function SelectModelDialog() {
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
  const { model } = useImageGeneration();

  return (
    <Dialog open={isModelDialogOpen} onOpenChange={setIsModelDialogOpen}>
      <div className='relative w-full min-h-[93px] rounded-t-lg overflow-hidden flex items-center justify-center'>
        {/* Background image */}
        <Image
          src='/generate-image-sidebar.png'
          alt='generate image sidebar'
          fill
          className='object-cover z-0 rounded-b-none'
          priority
        />
        <DialogTrigger asChild>
          <Button
            className='relative z-10 min-h-[70px] w-[90%] bg-gray-900/90 backdrop-blur-xs opacity-90 border-none rounded-lg px-4 flex items-center justify-between cursor-pointer'
            onMouseDown={() => setIsModelDialogOpen(true)}
          >
            <div className='flex flex-col justify-center text-left'>
              <div className='text-fuchsia-600 text-sm font-medium leading-tight'>
                Model / Preset
              </div>
              <span className='text-stone-50/90 text-lg font-semibold leading-tight mt-1 capitalize'>
                {model?.title}
              </span>
            </div>
            <ChevronRight className='w-4 h-4 text-stone-50/90' />
          </Button>
        </DialogTrigger>
      </div>
      <SelectModelDialogContent setIsModelDialogOpen={setIsModelDialogOpen} />
    </Dialog>
  );
}

function SelectModelDialogContent({
  setIsModelDialogOpen,
}: {
  setIsModelDialogOpen: (open: boolean) => void;
}) {
  const { model, setModel } = useImageGeneration();
  const { data: userModels, isLoading } = useModels();

  const handleModelSelect = (
    title: string,
    src: string,
    idx: number,
    modelId: string,
    triggerWord?: string,
    modelUrl?: string,
    isTraining = false,
  ) => {
    if (isTraining) return; // Don't allow selection of training models
    setModel({ title, src, idx, triggerWord, modelUrl, modelId });
    setIsModelDialogOpen(false);
  };

  return (
    <DialogContent className='bg-neutral-800 border border-white/10 backdrop-blur-md rounded-lg !max-w-[1180px]'>
      <DialogHeader className='flex flex-row items-center justify-between'>
        <DialogTitle className='text-3xl font-semibold text-stone-50/90'>
          Model / Preset
        </DialogTitle>
      </DialogHeader>
      <Separator className='bg-white/10 !w-[70%]' />
      <div className='py-4 max-h-[580px] overflow-y-auto'>
        <div className='grid grid-cols-4 gap-y-4'>
          {/* User Models Section */}
          {isLoading && (
            <div className='col-span-4 flex items-center justify-center py-8'>
              <Loader2 className='w-8 h-8 text-white animate-spin' />
            </div>
          )}
          {/* User Models Section */}
          {!isLoading && (
            <>
              {userModels && userModels.length > 0 ? (
                userModels.map((userModel) => {
                  const modelStatus = getModelStatus(userModel);
                  const isTraining = modelStatus === 'training';
                  const modelTitle =
                    userModel.model_name ||
                    userModel.trigger_word ||
                    'Untitled Model';
                  const modelSrc = userModel.model_url || null;
                  const isSelected =
                    model?.title === modelTitle &&
                    model?.src === (modelSrc || '');

                  return (
                    <div
                      key={userModel.id}
                      className={cn(
                        'group relative w-[264px] h-[264px] rounded-xl overflow-hidden bg-neutral-900 cursor-pointer border',
                        isSelected
                          ? 'border-2 border-fuchsia-600'
                          : 'border-white/10',
                        isTraining && 'opacity-60 cursor-not-allowed',
                      )}
                      onMouseDown={() =>
                        handleModelSelect(
                          modelTitle,
                          modelSrc || '',
                          -1,
                          userModel.id,
                          userModel.trigger_word || undefined,
                          userModel.model_url || undefined,
                          isTraining,
                        )
                      }
                    >
                      <div className='w-full h-full bg-neutral-800 flex items-center justify-center'>
                        <ImageIcon className='w-16 h-16 text-white/60' />
                      </div>

                      {/* Training Badge */}
                      {isTraining && (
                        <div className='absolute top-4 left-4 z-10'>
                          <Badge className='bg-yellow-600/90 text-white text-xs font-medium px-2 py-1 rounded-md flex items-center gap-1'>
                            <Loader2 className='w-3 h-3 animate-spin' />
                            Training
                          </Badge>
                        </div>
                      )}

                      {/* Countdown Timer for Training Models */}
                      {isTraining &&
                        userModel.training_started_at &&
                        userModel.training_finished_at && (
                          <div className='absolute top-4 right-4 z-10'>
                            <CountdownTimer
                              startTime={userModel.training_started_at}
                              endTime={userModel.training_finished_at}
                              compact
                            />
                          </div>
                        )}

                      {isSelected && !isTraining && (
                        <div className='absolute top-4 right-4 z-10 w-7 h-7 bg-fuchsia-600 rounded-full flex items-center justify-center shadow-lg'>
                          <Check
                            className='w-4 h-4 text-white'
                            strokeWidth={3}
                          />
                        </div>
                      )}

                      <div className='absolute bottom-0 left-0 w-full bg-black/60 backdrop-blur-xs py-2 px-3 flex items-center justify-center'>
                        <Label className='text-white text-base font-medium truncate drop-shadow text-center'>
                          {modelTitle}
                        </Label>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className='col-span-4 text-center py-4 text-stone-400'>
                  No custom models yet. Train your first model to see it here.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DialogContent>
  );
}
